import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Smartphone, ShieldAlert, Code2, UserCircle, Send, CheckCircle2, AlertTriangle, Loader2, ShieldCheck } from 'lucide-react';
type Message = {
  id: string;
  role: 'user' | 'agent' | 'system';
  content: string;
  toolCall?: any;
};
type Scenario = 'A' | 'B' | 'C';
const SCENARIOS = {
  A: {
    id: 'A',
    name: 'HERO: eSIM Activation',
    desc: 'Device setup + secure eSIM provisioning'
  },
  B: {
    id: 'B',
    name: 'Broadband Outage',
    desc: 'Triage, diagnostics, and technician scheduling.'
  },
  C: {
    id: 'C',
    name: 'Billing Shock',
    desc: 'Roaming charge explanation and resolution'
  }
};
const MOCK_FLOW_A: Message[] = [{
  id: '1',
  role: 'user',
  content: 'Hi, I just got a new iPhone 15 and need to set it up. I think I need an eSIM?'
}, {
  id: '2',
  role: 'agent',
  content: 'Hello! I can certainly help you set up your new iPhone 15 and activate an eSIM. To get started securely, I just sent a one-time passcode to the phone number ending in 4092. Could you please share that code with me?',
  toolCall: {
    name: 'send_otp',
    status: 'success'
  }
}, {
  id: '3',
  role: 'user',
  content: "Sure, it's 849302"
}, {
  id: '4',
  role: 'agent',
  content: "Thank you. I've verified your account.",
  toolCall: {
    name: 'verify_customer_otp',
    status: 'success',
    data: {
      verified: true,
      accountId: 'ACC-9928'
    }
  }
}, {
  id: '5',
  role: 'system',
  content: 'Guardrail Passed: Identity Verified'
}, {
  id: '6',
  role: 'agent',
  content: "I see you're on the 'Unlimited Premium' plan, which fully supports eSIM. Before I generate the new eSIM, I need to confirm: this will deactivate the physical SIM in your old device. Are you ready to proceed?",
  toolCall: {
    name: 'fetch_device_entitlement',
    status: 'success',
    data: {
      eligible: true,
      plan: 'Unlimited Premium'
    }
  }
}, {
  id: '7',
  role: 'user',
  content: 'Yes, go ahead.'
}, {
  id: '8',
  role: 'system',
  content: 'Guardrail Passed: Confirmation Gate'
}, {
  id: '9',
  role: 'agent',
  content: "Great. I'm issuing your new eSIM now...",
  toolCall: {
    name: 'issue_esim',
    status: 'pending'
  }
}, {
  id: '10',
  role: 'agent',
  content: "Your eSIM has been successfully activated! You should see a prompt on your iPhone 15 to complete the setup. I've also logged this update in your account and sent a confirmation email.",
  toolCall: {
    name: 'activate_esim',
    status: 'success',
    data: {
      iccid: '894450...123',
      network: 'ACME'
    }
  }
}, {
  id: '11',
  role: 'agent',
  content: 'Before you go — on a scale of 1 to 5, how would you rate the support you received today?'
}, {
  id: '12',
  role: 'user',
  content: '5 — that was really smooth, thanks!'
}, {
  id: '13',
  role: 'system',
  content: 'CSAT Captured: 5/5'
}, {
  id: '14',
  role: 'agent',
  content: 'Brilliant, thank you for the feedback! Enjoy your new device. Have a great day.'
}];
const MOCK_FLOW_B: Message[] = [{
  id: '1',
  role: 'user',
  content: 'My internet has been down since this morning. Nothing is working.'
}, {
  id: '2',
  role: 'agent',
  content: 'Hello! I can help you with your broadband issue. To get started securely, I just sent a one-time passcode to your registered mobile number. Could you please share that code with me?',
  toolCall: {
    name: 'send_otp',
    status: 'success'
  }
}, {
  id: '3',
  role: 'user',
  content: 'It is 492811'
}, {
  id: '4',
  role: 'agent',
  content: 'Thank you. I have verified your account.',
  toolCall: {
    name: 'verify_customer_otp',
    status: 'success',
    data: {
      verified: true,
      accountId: 'ACC-7741'
    }
  }
}, {
  id: '5',
  role: 'system',
  content: 'Guardrail Passed: Identity Verified'
}, {
  id: '6',
  role: 'agent',
  content: 'I am running some remote diagnostics on your line now to see what is going on...',
  toolCall: {
    name: 'run_line_diagnostics',
    status: 'success',
    data: {
      status: 'fault_detected',
      type: 'exchange_outage',
      eta: '4 hours',
      affected_area: 'SW11'
    }
  }
}, {
  id: '7',
  role: 'agent',
  content: 'I can see there is a known exchange outage affecting your area (SW11). Our engineers are already on site.',
  toolCall: {
    name: 'check_outage_map',
    status: 'success',
    data: {
      outage_id: 'OUT-2847',
      region: 'SW11',
      estimated_restore: '14:00 GMT'
    }
  }
}, {
  id: '8',
  role: 'user',
  content: 'Can I get a credit for the downtime?'
}, {
  id: '9',
  role: 'agent',
  content: 'Absolutely. I have issued a pro-rata credit to your account for the disruption.',
  toolCall: {
    name: 'issue_service_credit',
    status: 'success',
    data: {
      amount: '£4.50',
      reason: 'service_outage',
      applied_to: 'next_bill'
    }
  }
}, {
  id: '10',
  role: 'agent',
  content: 'The £4.50 credit has been applied to your next bill. Would you like me to send you an SMS when the service is fully restored?'
}, {
  id: '11',
  role: 'user',
  content: 'Yes please, that would be great.'
}, {
  id: '12',
  role: 'agent',
  content: 'Done! I have registered your number for a service restoration SMS. You will receive a text as soon as connectivity is confirmed in your area.',
  toolCall: {
    name: 'register_sms_alert',
    status: 'success',
    data: {
      alert_type: 'service_restored',
      number_ending: '7741',
      outage_id: 'OUT-2847'
    }
  }
}, {
  id: '13',
  role: 'agent',
  content: 'Is there anything else I can help you with today?'
}, {
  id: '14',
  role: 'user',
  content: 'No, that is everything. Thanks for the quick help!'
}, {
  id: '15',
  role: 'agent',
  content: 'You are welcome! Before you go — on a scale of 1 to 5, how would you rate the support you received today?'
}, {
  id: '16',
  role: 'user',
  content: '4 — really helpful, just wish the outage was fixed sooner!'
}, {
  id: '17',
  role: 'system',
  content: 'CSAT Captured: 4/5'
}, {
  id: '18',
  role: 'agent',
  content: 'Thank you for the feedback! I completely understand. I hope your service is restored soon. Have a great day.'
}];
const MOCK_FLOW_C: Message[] = [{
  id: '1',
  role: 'user',
  content: 'I just got my bill and there are £87 in roaming charges! I was only in France for 3 days!'
}, {
  id: '2',
  role: 'agent',
  content: 'I understand that unexpected charges can be frustrating. I can certainly look into this for you. To access your billing details securely, I just sent a one-time passcode to your device. Could you please share that code?',
  toolCall: {
    name: 'send_otp',
    status: 'success'
  }
}, {
  id: '3',
  role: 'user',
  content: '559201'
}, {
  id: '4',
  role: 'agent',
  content: 'Thank you. I have verified your account.',
  toolCall: {
    name: 'verify_customer_otp',
    status: 'success',
    data: {
      verified: true,
      accountId: 'ACC-3356'
    }
  }
}, {
  id: '5',
  role: 'system',
  content: 'Guardrail Passed: Identity Verified'
}, {
  id: '6',
  role: 'agent',
  content: 'Let me pull up the details for your recent trip to France...',
  toolCall: {
    name: 'lookup_billing_details',
    status: 'success',
    data: {
      charges: [{
        type: 'EU_roaming_data',
        amount: 62.0,
        usage: '4.2GB'
      }, {
        type: 'international_calls',
        amount: 25.0,
        minutes: 45
      }],
      period: 'Mar 12-14'
    }
  }
}, {
  id: '7',
  role: 'agent',
  content: 'I can see the breakdown. Your plan includes 5GB of EU roaming data, but you used 4.2GB of additional data beyond that allowance (£62). There were also 45 minutes of international calls (£25).'
}, {
  id: '8',
  role: 'user',
  content: 'But I thought EU roaming was included in my plan!'
}, {
  id: '9',
  role: 'system',
  content: 'Guardrail: Escalation sentiment detected — maintaining empathetic tone'
}, {
  id: '10',
  role: 'agent',
  content: 'I completely understand the confusion. Your plan includes 12GB domestically, but the EU roaming cap is 5GB. As a one-time courtesy, I can apply a goodwill credit for the data overage.',
  toolCall: {
    name: 'apply_goodwill_credit',
    status: 'success',
    data: {
      amount: 40.0,
      reason: 'first_time_roaming_shock',
      approval: 'auto_policy'
    }
  }
}, {
  id: '11',
  role: 'agent',
  content: 'I have applied a £40 credit to your account, so the remaining balance for the roaming is £47. For future trips, would you like me to add the Travel Booster (£6/month) for unlimited EU roaming?'
}, {
  id: '12',
  role: 'user',
  content: 'Yes, that sounds fair. Please go ahead with the credit and add the Travel Booster.'
}, {
  id: '13',
  role: 'system',
  content: 'Policy Check Passed: Credit amount £40 within auto-approval threshold (max £50)'
}, {
  id: '14',
  role: 'agent',
  content: 'The £40 goodwill credit has been confirmed on your account and the Travel Booster add-on (£6/month) is now active. You will see the credit reflected on your next bill.',
  toolCall: {
    name: 'add_plan_addon',
    status: 'success',
    data: {
      addon: 'Travel Booster',
      monthly_cost: '£6.00',
      effective_from: 'immediate',
      includes: 'Unlimited EU roaming data & calls'
    }
  }
}, {
  id: '15',
  role: 'agent',
  content: 'Is there anything else I can help you with today?'
}, {
  id: '16',
  role: 'user',
  content: 'No, that covers it. Thanks for sorting this out so quickly.'
}, {
  id: '17',
  role: 'agent',
  content: 'You are welcome! Before you go — on a scale of 1 to 5, how would you rate the support you received today?'
}, {
  id: '18',
  role: 'user',
  content: '5 — really appreciate you sorting the credit so quickly.'
}, {
  id: '19',
  role: 'system',
  content: 'CSAT Captured: 5/5'
}, {
  id: '20',
  role: 'agent',
  content: 'Thank you for the feedback! Enjoy your next trip worry-free. Have a great day.'
}];
const MOCK_FLOWS: Record<Scenario, Message[]> = {
  A: MOCK_FLOW_A,
  B: MOCK_FLOW_B,
  C: MOCK_FLOW_C
};
export function DemoPage() {
  const [activeScenario, setActiveScenario] = useState<Scenario>('A');
  const [messages, setMessages] = useState<Message[]>([]);
  const [step, setStep] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [activePanel, setActivePanel] = useState<'trace' | 'guardrails' | 'handoff'>('trace');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setMessages([]);
    setStep(0);
    advanceFlow(0, activeScenario);
  }, [activeScenario]);
  const advanceFlow = (currentStep: number, scenario: Scenario = activeScenario) => {
    const flow = MOCK_FLOWS[scenario];
    if (currentStep >= flow.length) return;
    const nextMsg = flow[currentStep];
    if (nextMsg.role === 'agent') {
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        setMessages(prev => [...prev, nextMsg]);
        setStep(currentStep + 1);
      }, 1500);
    } else {
      setMessages(prev => [...prev, nextMsg]);
      setStep(currentStep + 1);
    }
  };
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages, isTyping]);
  const handleNextStep = () => {
    if (!isTyping) advanceFlow(step);
  };
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') return;
      if (e.key === 'n' || e.key === 'N') {
        handleNextStep();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [step, isTyping]);
  const latestToolCall = [...messages].reverse().find(m => m.toolCall)?.toolCall;
  return <div className="w-full h-[85vh] flex flex-col bg-white rounded-2xl sierra-shadow border border-gray-200 overflow-hidden" data-id="element-205">
      <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50" data-id="element-206">
        <div className="flex gap-2" data-id="element-207">
          {(Object.keys(SCENARIOS) as Scenario[]).map(s => <button key={s} onClick={() => setActiveScenario(s)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeScenario === s ? 'bg-sierra-blue text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'}`} data-id="element-208">
              {SCENARIOS[s].name}
            </button>)}
        </div>
        <div className="flex gap-2" data-id="element-209">
          <button onClick={() => setActivePanel('trace')} className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 ${activePanel === 'trace' ? 'bg-gray-200 text-gray-800' : 'text-gray-500 hover:bg-gray-100'}`} data-id="element-210">
            <Code2 className="w-3.5 h-3.5" data-id="element-211" /> Trace
          </button>
          <button onClick={() => setActivePanel('guardrails')} className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 ${activePanel === 'guardrails' ? 'bg-gray-200 text-gray-800' : 'text-gray-500 hover:bg-gray-100'}`} data-id="element-212">
            <ShieldAlert className="w-3.5 h-3.5" data-id="element-213" /> Guardrails
          </button>
          <button onClick={() => setActivePanel('handoff')} className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 ${activePanel === 'handoff' ? 'bg-gray-200 text-gray-800' : 'text-gray-500 hover:bg-gray-100'}`} data-id="element-214">
            <UserCircle className="w-3.5 h-3.5" data-id="element-215" /> Handoff
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden" data-id="element-216">
        <div className="flex-1 flex flex-col bg-[#FAFAFA] relative" data-id="element-217">
          <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-218">
            <AnimatePresence data-id="element-219">
              {messages.map(msg => <motion.div key={msg.id} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-220">
                  {msg.role === 'system' ? <div className="w-full flex justify-center my-2" data-id="element-221">
                      <span className="px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-full flex items-center gap-1.5 border border-emerald-100" data-id="element-222">
                        <ShieldCheck className="w-3.5 h-3.5" data-id="element-223" />
                        {msg.content}
                      </span>
                    </div> : <div className={`max-w-[80%] rounded-2xl p-4 ${msg.role === 'user' ? 'bg-sierra-blue text-white rounded-br-sm' : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm sierra-shadow'}`} data-id="element-224">
                      <p className="text-sm leading-relaxed" data-id="element-225">{msg.content}</p>
                    </div>}
                </motion.div>)}
              {isTyping && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} className="flex justify-start" data-id="element-226">
                  <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm p-4 flex items-center gap-2 sierra-shadow" data-id="element-227">
                    <Loader2 className="w-4 h-4 text-sierra-blue animate-spin" data-id="element-228" />
                    <span className="text-xs text-gray-500 font-medium" data-id="element-229">
                      Agent is thinking...
                    </span>
                  </div>
                </motion.div>}
            </AnimatePresence>

            <div ref={messagesEndRef} data-id="element-230" />
          </div>
          <div className="p-4 bg-white border-t border-gray-200" data-id="element-231">
            <div className="relative flex items-center" data-id="element-232">
              <input type="text" disabled placeholder={step < MOCK_FLOWS[activeScenario].length ? "Press 'Next Step' to advance demo..." : 'Demo complete.'} className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none" data-id="element-233" />
              <button onClick={handleNextStep} disabled={isTyping || step >= MOCK_FLOWS[activeScenario].length} className="absolute right-2 p-2 bg-sierra-blue text-white rounded-lg disabled:opacity-50 hover:bg-green-800 transition-colors flex items-center gap-2 text-sm font-medium" data-id="element-234">
                Next Step <Send className="w-4 h-4" data-id="element-235" />
              </button>
            </div>
          </div>
        </div>
        <div className="w-80 border-l border-gray-200 bg-white flex flex-col" data-id="element-236">
          <div className="p-4 border-b border-gray-100 bg-gray-50" data-id="element-237">
            <h3 className="font-semibold text-sm text-gray-800 flex items-center gap-2" data-id="element-238">
              {activePanel === 'trace' && <>
                  <Code2 className="w-4 h-4 text-sierra-blue" data-id="element-239" /> Tool Trace
                </>}
              {activePanel === 'guardrails' && <>
                  <ShieldAlert className="w-4 h-4 text-emerald-600" data-id="element-240" />{' '}
                  Guardrails
                </>}
              {activePanel === 'handoff' && <>
                  <UserCircle className="w-4 h-4 text-purple-600" data-id="element-241" /> Human
                  Handoff
                </>}
            </h3>
          </div>

          <div className="flex-1 overflow-y-auto p-4" data-id="element-242">
            {activePanel === 'trace' && <div className="space-y-4" data-id="element-243">
                {latestToolCall ? <div className="bg-gray-900 rounded-xl p-4 text-xs font-mono text-gray-300 overflow-x-auto" data-id="element-244">
                    <div className="text-green-400 mb-2" data-id="element-245">Tool Execution</div>
                    <div className="text-white font-semibold mb-2" data-id="element-246">
                      {latestToolCall.name}()
                    </div>
                    <div className="text-gray-500 mb-1" data-id="element-247">
                      Status:{' '}
                      <span className={latestToolCall.status === 'success' ? 'text-green-400' : 'text-amber-400'} data-id="element-248">
                        {latestToolCall.status}
                      </span>
                    </div>
                    {latestToolCall.data && <pre className="mt-2 text-gray-400" data-id="element-249">
                        {JSON.stringify(latestToolCall.data, null, 2)}
                      </pre>}
                  </div> : <div className="text-sm text-gray-500 text-center mt-10" data-id="element-250">
                    Waiting for tool execution...
                  </div>}
                <div className="pt-4 border-t border-gray-100" data-id="element-251">
                  <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2" data-id="element-252">
                    Available Tools
                  </h4>
                  <ul className="text-xs text-gray-600 space-y-1.5" data-id="element-253">
                    <li className="flex items-center gap-1.5" data-id="element-254">
                      <CheckCircle2 className="w-3 h-3 text-green-500" data-id="element-255" />{' '}
                      verify_customer_otp
                    </li>
                    {activeScenario === 'A' && <>
                        <li className="flex items-center gap-1.5" data-id="element-256">
                          <CheckCircle2 className="w-3 h-3 text-green-500" data-id="element-257" />{' '}
                          lookup_account (SFDC)
                        </li>
                        <li className="flex items-center gap-1.5" data-id="element-258">
                          <CheckCircle2 className="w-3 h-3 text-green-500" data-id="element-259" />{' '}
                          fetch_device_entitlement
                        </li>
                        <li className="flex items-center gap-1.5" data-id="element-260">
                          <CheckCircle2 className="w-3 h-3 text-green-500" data-id="element-261" />{' '}
                          issue_esim
                        </li>
                        <li className="flex items-center gap-1.5" data-id="element-262">
                          <CheckCircle2 className="w-3 h-3 text-green-500" data-id="element-263" />{' '}
                          activate_esim
                        </li>
                      </>}
                    {activeScenario === 'B' && <>
                        <li className="flex items-center gap-1.5" data-id="element-264">
                          <CheckCircle2 className="w-3 h-3 text-green-500" data-id="element-265" />{' '}
                          run_line_diagnostics
                        </li>
                        <li className="flex items-center gap-1.5" data-id="element-266">
                          <CheckCircle2 className="w-3 h-3 text-green-500" data-id="element-267" />{' '}
                          check_outage_map
                        </li>
                        <li className="flex items-center gap-1.5" data-id="element-268">
                          <CheckCircle2 className="w-3 h-3 text-green-500" data-id="element-269" />{' '}
                          issue_service_credit
                        </li>
                        <li className="flex items-center gap-1.5" data-id="element-270">
                          <CheckCircle2 className="w-3 h-3 text-green-500" data-id="element-271" />{' '}
                          register_sms_alert
                        </li>
                      </>}
                    {activeScenario === 'C' && <>
                        <li className="flex items-center gap-1.5" data-id="element-272">
                          <CheckCircle2 className="w-3 h-3 text-green-500" data-id="element-273" />{' '}
                          lookup_billing_details
                        </li>
                        <li className="flex items-center gap-1.5" data-id="element-274">
                          <CheckCircle2 className="w-3 h-3 text-green-500" data-id="element-275" />{' '}
                          check_credit_policy
                        </li>
                        <li className="flex items-center gap-1.5" data-id="element-276">
                          <CheckCircle2 className="w-3 h-3 text-green-500" data-id="element-277" />{' '}
                          apply_goodwill_credit
                        </li>
                        <li className="flex items-center gap-1.5" data-id="element-278">
                          <CheckCircle2 className="w-3 h-3 text-green-500" data-id="element-279" />{' '}
                          add_plan_addon
                        </li>
                      </>}
                  </ul>
                </div>
              </div>}
            {activePanel === 'guardrails' && <>
                <div className="space-y-3" data-id="element-280">
                  <div className="p-3 rounded-lg border border-emerald-200 bg-emerald-50 flex items-start gap-3" data-id="element-281">
                    <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0" data-id="element-282" />
                    <div data-id="element-283">
                      <div className="text-sm font-semibold text-emerald-800" data-id="element-284">
                        PII Redaction
                      </div>
                      <div className="text-xs text-emerald-600 mt-0.5" data-id="element-285">
                        Active. Phone numbers and account IDs masked in logs.
                      </div>
                    </div>
                  </div>
                  <div className={`p-3 rounded-lg border ${step > 3 ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-gray-50'} flex items-start gap-3 transition-colors`} data-id="element-286">
                    <ShieldCheck className={`w-5 h-5 ${step > 3 ? 'text-emerald-600' : 'text-gray-400'} shrink-0`} data-id="element-287" />
                    <div data-id="element-288">
                      <div className={`text-sm font-semibold ${step > 3 ? 'text-emerald-800' : 'text-gray-600'}`} data-id="element-289">
                        Auth Required
                      </div>
                      <div className={`text-xs mt-0.5 ${step > 3 ? 'text-emerald-600' : 'text-gray-500'}`} data-id="element-290">
                        OTP verification before account changes.
                      </div>
                    </div>
                  </div>

                  {activeScenario === 'A' && <div className={`p-3 rounded-lg border ${step > 7 ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-gray-50'} flex items-start gap-3 transition-colors`} data-id="element-291">
                      <AlertTriangle className={`w-5 h-5 ${step > 7 ? 'text-emerald-600' : 'text-gray-400'} shrink-0`} data-id="element-292" />
                      <div data-id="element-293">
                        <div className={`text-sm font-semibold ${step > 7 ? 'text-emerald-800' : 'text-gray-600'}`} data-id="element-294">
                          Confirmation Gate
                        </div>
                        <div className={`text-xs mt-0.5 ${step > 7 ? 'text-emerald-600' : 'text-gray-500'}`} data-id="element-295">
                          Explicit user consent required for eSIM issue.
                        </div>
                      </div>
                    </div>}

                  {activeScenario === 'B' && <div className={`p-3 rounded-lg border ${step > 7 ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-gray-50'} flex items-start gap-3 transition-colors`} data-id="element-296">
                      <ShieldCheck className={`w-5 h-5 ${step > 7 ? 'text-emerald-600' : 'text-gray-400'} shrink-0`} data-id="element-297" />
                      <div data-id="element-298">
                        <div className={`text-sm font-semibold ${step > 7 ? 'text-emerald-800' : 'text-gray-600'}`} data-id="element-299">
                          Proactive Credit Policy
                        </div>
                        <div className={`text-xs mt-0.5 ${step > 7 ? 'text-emerald-600' : 'text-gray-500'}`} data-id="element-300">
                          Automatic pro-rata credit approved for known outages.
                        </div>
                      </div>
                    </div>}

                  {activeScenario === 'C' && <>
                      <div className={`p-3 rounded-lg border ${step > 7 ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-gray-50'} flex items-start gap-3 transition-colors`} data-id="element-301">
                        <AlertTriangle className={`w-5 h-5 ${step > 7 ? 'text-emerald-600' : 'text-gray-400'} shrink-0`} data-id="element-302" />
                        <div data-id="element-303">
                          <div className={`text-sm font-semibold ${step > 7 ? 'text-emerald-800' : 'text-gray-600'}`} data-id="element-304">
                            Sentiment Monitoring
                          </div>
                          <div className={`text-xs mt-0.5 ${step > 7 ? 'text-emerald-600' : 'text-gray-500'}`} data-id="element-305">
                            Escalation detected. Tone adjusted to empathetic.
                          </div>
                        </div>
                      </div>
                      <div className={`p-3 rounded-lg border ${step > 11 ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-gray-50'} flex items-start gap-3 transition-colors`} data-id="element-306">
                        <ShieldCheck className={`w-5 h-5 ${step > 11 ? 'text-emerald-600' : 'text-gray-400'} shrink-0`} data-id="element-307" />
                        <div data-id="element-308">
                          <div className={`text-sm font-semibold ${step > 11 ? 'text-emerald-800' : 'text-gray-600'}`} data-id="element-309">
                            Policy Check
                          </div>
                          <div className={`text-xs mt-0.5 ${step > 11 ? 'text-emerald-600' : 'text-gray-500'}`} data-id="element-310">
                            Credit policy lookup: £40 within auto-approval
                            threshold (max £50).
                          </div>
                        </div>
                      </div>
                    </>}
                </div>
              </>}
            {activePanel === 'handoff' && <>
                <div className="space-y-4" data-id="element-311">
                  <p className="text-xs text-gray-500 mb-4" data-id="element-312">
                    If the agent escalates, this is the context package sent to
                    the human agent in Salesforce/ServiceNow.
                  </p>
                  <div className="bg-purple-50 border border-purple-100 rounded-xl p-4" data-id="element-313">
                    <h4 className="text-xs font-bold text-purple-800 uppercase mb-2" data-id="element-314">
                      Summary
                    </h4>
                    <p className="text-sm text-purple-900 mb-4" data-id="element-315">
                      {activeScenario === 'A' && 'Customer requested eSIM activation for new iPhone 15. Identity verified via OTP. Entitlement checked.'}
                      {activeScenario === 'B' && 'Customer reported broadband outage in SW11. Identity verified via OTP. Diagnostics run and outage confirmed.'}
                      {activeScenario === 'C' && 'Customer queried £87 EU roaming charges. Identity verified via OTP. Billing details checked.'}
                    </p>

                    <h4 className="text-xs font-bold text-purple-800 uppercase mb-2" data-id="element-316">
                      Actions Taken
                    </h4>
                    <ul className="text-xs text-purple-900 space-y-1 mb-4 list-disc pl-4" data-id="element-317">
                      <li data-id="element-318">OTP Verified (Success)</li>
                      {activeScenario === 'A' && <>
                          <li data-id="element-319">Plan Eligibility Checked (Success)</li>
                          {step > 8 && <li data-id="element-320">eSIM Issued (Success)</li>}
                        </>}
                      {activeScenario === 'B' && <>
                          <li data-id="element-321">Diagnostics Run (Success)</li>
                          {step > 7 && <li data-id="element-322">Credit Issued (Success)</li>}
                          {step > 10 && <li data-id="element-323">SMS Alert Registered (Success)</li>}
                        </>}
                      {activeScenario === 'C' && <>
                          <li data-id="element-324">Billing Details Looked Up (Success)</li>
                          {step > 9 && <li data-id="element-325">Goodwill Credit Applied (Success)</li>}
                          {step > 12 && <li data-id="element-326">Travel Booster Added (Success)</li>}
                        </>}
                    </ul>

                    <h4 className="text-xs font-bold text-purple-800 uppercase mb-2" data-id="element-327">
                      Recommended Next Step
                    </h4>
                    <div className="bg-white p-2 rounded border border-purple-200 text-xs text-gray-700" data-id="element-328">
                      {activeScenario === 'A' && (step < 9 ? 'Assist with eSIM provisioning manually.' : 'Confirm device connection. No further action needed.')}
                      {activeScenario === 'B' && (step < 8 ? 'Assist with outage credit or technician scheduling.' : 'Monitor outage OUT-2847. No immediate action required.')}
                      {activeScenario === 'C' && (step < 10 ? 'Review roaming charges and apply credit if applicable.' : 'Confirm credit on next bill. No further action needed.')}
                    </div>
                  </div>
                </div>
              </>}
          </div>
        </div>
      </div>
    </div>;
}